import React, { useState } from 'react';
import { LockIcon, MailIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
interface LoginPageProps {
  onLogin: () => void;
}
export function LoginPage({
  onLogin
}: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      onLogin();
    }, 800);
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-978">
      <div className="w-full max-w-md" data-id="element-979">
        {/* Logo/Brand */}
        <div className="text-center mb-8" data-id="element-980">
          <div className="inline-flex items-center justify-center mb-4" data-id="element-981">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4Kv3v8GHQWjwS3xkZwFMvx/pasted-image.png" alt="Real Estream" className="h-16" data-id="element-982" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-983">Welcome Back</h1>
          <p className="text-gray-600 mt-2" data-id="element-984">Sign in to access your dashboard</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8" data-id="element-985">
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-986">
            {/* Email Field */}
            <div data-id="element-987">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-988">
                Email Address
              </label>
              <div className="relative" data-id="element-989">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-990">
                  <MailIcon size={18} className="text-gray-400" data-id="element-991" />
                </div>
                <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors" placeholder="you@example.com" disabled={isLoading} data-id="element-992" />
              </div>
            </div>

            {/* Password Field */}
            <div data-id="element-993">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-994">
                Password
              </label>
              <div className="relative" data-id="element-995">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-996">
                  <LockIcon size={18} className="text-gray-400" data-id="element-997" />
                </div>
                <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors" placeholder="Enter your password" disabled={isLoading} data-id="element-998" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center" disabled={isLoading} data-id="element-999">
                  {showPassword ? <EyeOffIcon size={18} className="text-gray-400 hover:text-gray-600" data-id="element-1000" /> : <EyeIcon size={18} className="text-gray-400 hover:text-gray-600" data-id="element-1001" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-id="element-1002">
                {error}
              </div>}

            {/* Login Button */}
            <button type="submit" disabled={isLoading} className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1003">
              {isLoading ? <span className="flex items-center justify-center" data-id="element-1004">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1005">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1006"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1007"></path>
                  </svg>
                  Signing in...
                </span> : 'Sign In'}
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-1008">
            <p className="text-xs text-gray-500 text-center" data-id="element-1009">
              Demo: Use any email and password to sign in
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-8" data-id="element-1010">
          © 2025 Real Estream. All rights reserved.
        </p>
      </div>
    </div>;
}